import { Observable } from 'rxjs';
import { OrderedMap, OrderedSet } from 'immutable';
import { ActionsObservable } from 'redux-observable';
import { formValueSelector } from 'redux-form/immutable';
import _ from 'lodash';

import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import { selectLocations } from 'entities/location/selectors';
import { addMultipleAction, setPendingExternalTotalAction } from 'entities/donation/actions';
import DonationsApi from 'entities/donation/api';
import LocationsApi from 'entities/location/api';
import Donation from 'entities/donation/model';
import { selectUser } from 'entities/user/selectors';

import {
  respondSuccess,
  respondFailed,
  APPROVE,
  DENY,
  ApproveAction,
  DenyAction,
  SET_FETCH_PARAMS,
  FetchParamsAction,
  fetchSuccessAction,
  fetchFailedAction,
  setFetchParamsAction,
  RespondSuccess,
  FORM_IDENTIFIER,
  MATCHING_REQUEST_STATUS_DATE_FIELD,
  MATCHING_REQUEST_STATUSES,
  setDisplayedDonationIdsAction as setDisplayedExternalDonationRequestIdsAction,
  setTotalAction as setDisplayedExternalDonationTotalAction,
  InitAction,
  INIT,
} from './types';
import { selectFetchParams } from './selectors';
import { Store } from 'redux';
import { State } from 'reducers';
import { selectPendingExternalTotal } from 'entities/donation/selectors';

const init = (action$: ActionsObservable<InitAction>, store: Store<State>) =>
  action$.ofType(INIT).mergeMap(() => {
    const state = store.getState();

    const currentTotal = selectPendingExternalTotal(state);
    if (currentTotal !== null) {
      return Observable.empty();
    }

    const pendingExternalDonationMatchRequestsAction =
      DonationsApi.fetchPendingExternalDonationCount().catch(() =>
        Observable.of(
          showErrorAction(
            'An error occurred during loading pending external donation match request count.'
          )
        )
      );
    return pendingExternalDonationMatchRequestsAction;
  });

const fetch = (action$: ActionsObservable<FetchParamsAction | RespondSuccess>, store: any) =>
  action$.ofType(SET_FETCH_PARAMS).mergeMap(() => {
    const state = store.getState();
    const fetchParams = selectFetchParams(state);
    const pageNum = fetchParams.get('pageNum') || 0;
    const limit = fetchParams.get('limit');
    const skip = limit * pageNum;
    const matchingRequestStatus: MATCHING_REQUEST_STATUSES =
      fetchParams.get('matchingRequestStatus');
    const sort = fetchParams.get('sort');
    const order = fetchParams.get('order');
    const search = fetchParams.get('search');

    const selector = formValueSelector(FORM_IDENTIFIER);
    const plainState = state.toJS();
    const from = selector(plainState, 'from');
    const to = selector(plainState, 'to');
    const providers = selector(plainState, 'providers');
    const currencies = selector(plainState, 'currencies');
    const confidenceScore = selector(plainState, 'confidenceScore');
    const amountGte = selector(plainState, 'amountGte');
    const amountLte = selector(plainState, 'amountLte');
    const locations = selectLocations(state);

    const user = selectUser(state);

    const amountCurrencyCode = user?.displayCurrency;

    const actions = [];

    if (locations.size === 0) {
      actions.push(LocationsApi.fetchAll());
    }

    actions.push(
      DonationsApi.fetchExternal({
        limit,
        skip,
        matchingRequestStatus,
        sort,
        order,
        from: from?.valueOf(),
        to: to?.valueOf(),
        dateQueryScope: MATCHING_REQUEST_STATUS_DATE_FIELD[matchingRequestStatus],
        providers,
        currencies,
        confidenceScore,
        search,
        amountGte,
        amountLte,
        amountCurrencyCode,
      }).map((results) =>
        [
          addMultipleAction(
            OrderedMap(
              results?.donations?.map((donation) => [donation.id, new Donation(donation)]) ?? []
            )
          ),
          setDisplayedExternalDonationRequestIdsAction(
            OrderedSet(results?.donations?.map((donation) => donation.id) ?? [])
          ),
          'total' in results && setDisplayedExternalDonationTotalAction(results.total as number),
        ].filter(Boolean)
      )
    );

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), fetchSuccessAction()])
      .takeUntil(action$.ofType(SET_FETCH_PARAMS))
      .catch((e) => [fetchFailedAction(e), showErrorAction('Loading Failed!')]);
  });

const approve = (action$: ActionsObservable<ApproveAction>, store: Store<State>) =>
  action$.ofType(APPROVE).mergeMap(({ donationIds }) =>
    DonationsApi.approveOrDenyExternalDonations(donationIds, true)
      .mergeMap((resultingAction) => {
        const state = store.getState();
        const currentTotal = selectPendingExternalTotal(state);
        const newTotal = currentTotal >= donationIds.length ? currentTotal - donationIds.length : 0;
        return [
          resultingAction,
          respondSuccess(donationIds),
          showMessageAction('Donation matched'),
          setFetchParamsAction({}),
          setPendingExternalTotalAction(newTotal),
        ];
      })
      .catch(() => [respondFailed(donationIds), showErrorAction('Donation match approval failed')])
  );

const deny = (action$: ActionsObservable<DenyAction>, store: Store<State>) =>
  action$.ofType(DENY).mergeMap(({ donationIds, rejectionReason }) =>
    DonationsApi.approveOrDenyExternalDonations(donationIds, false, rejectionReason)
      .mergeMap((resultingAction) => {
        const state = store.getState();
        const currentTotal = selectPendingExternalTotal(state);
        const newTotal = currentTotal >= donationIds.length ? currentTotal - donationIds.length : 0;
        return [
          resultingAction,
          respondSuccess(donationIds),
          showMessageAction('Donation match denied'),
          setFetchParamsAction({}),
          setPendingExternalTotalAction(newTotal),
        ];
      })
      .catch(() => [
        respondFailed(donationIds),
        showErrorAction('Failed to deny donation match request'),
      ])
  );

export default [init, approve, deny, fetch];
